 

import React, { useState, useEffect, useRef } from "react"
import { gsap } from "gsap"
import "../styles/Rbt.css"
import Button from "../../shared/Button"

// Red-Black Tree Node class
class RBNode {
  constructor(value) {
    this.value = value
    this.color = "red"
    this.left = null
    this.right = null
    this.parent = null
    this.x = 0
    this.y = 0
    this.id = Math.random().toString(36).substring(2, 9)
  }
}

// Red-Black Tree implementation
class RedBlackTree {
  constructor() {
    this.NIL = new RBNode(-1)
    this.NIL.color = "black"
    this.root = this.NIL
    this.logs = []
  }

  // Check if a value exists in the tree
  contains(value) {
    let current = this.root

    while (current !== this.NIL) {
      if (value === current.value) {
        return true
      }

      if (value < current.value) {
        current = current.left
      } else {
        current = current.right
      }
    }

    return false
  }

  // Insert a new value into the tree
  insert(value) {
    // Check if value already exists
    if (this.contains(value)) {
      this.logs.push(`Ant ${value} already exists in the colony!`)
      return false
    }

    const newNode = new RBNode(value)
    newNode.left = this.NIL
    newNode.right = this.NIL

    let y = null
    let x = this.root

    // Find the position to insert the new node
    while (x !== this.NIL) {
      y = x
      if (newNode.value < x.value) {
        x = x.left
      } else {
        x = x.right
      }
    }

    newNode.parent = y

    if (y === null) {
      this.root = newNode
      this.logs.push(`Queen Ant ${value} takes the throne`)
    } else if (newNode.value < y.value) {
      y.left = newNode
      this.logs.push(`Ant ${value} joins the left of Ant ${y.value}`)
    } else {
      y.right = newNode
      this.logs.push(`Ant ${value} joins the right of Ant ${y.value}`)
    }

    // If the new node is a root node, color it black and return
    if (newNode.parent === null) {
      newNode.color = "black"
      this.logs.push(`Queen Ant ${value} dons the royal black`)
      return true
    }

    // If the grandparent is null, return
    if (newNode.parent.parent === null) {
      return true
    }

    // Fix the tree after insertion
    this.fixInsert(newNode)
    return true
  }

  // Fix the Red-Black Tree properties after insertion
  fixInsert(k) {
    let u

    while (k.parent !== null && k.parent.color === "red") {
      if (k.parent === k.parent.parent?.right) {
        u = k.parent.parent.left
        if (u !== null && u.color === "red") {
          // Case 1: Uncle is red
          u.color = "black"
          k.parent.color = "black"
          k.parent.parent.color = "red"
          this.logs.push(`Color swap: Ants ${u.value}, ${k.parent.value}, and ${k.parent.parent.value} change colors`)
          k = k.parent.parent
        } else {
          if (k === k.parent.left) {
            // Case 2: Uncle is black and node is a left child
            k = k.parent
            this.logs.push(`Right rotation: Ant ${k.value} moves up`)
            this.rightRotate(k)
          }
          // Case 3: Uncle is black and node is a right child
          if (k.parent !== null && k.parent.parent !== null) {
            k.parent.color = "black"
            k.parent.parent.color = "red"
            this.logs.push(`Left rotation: Ant ${k.parent.parent.value} steps down`)
            this.leftRotate(k.parent.parent)
          }
        }
      } else {
        u = k.parent.parent?.right || null

        if (u !== null && u.color === "red") {
          // Case 1: Uncle is red
          u.color = "black"
          k.parent.color = "black"
          k.parent.parent.color = "red"
          this.logs.push(`Color swap: Ants ${u.value}, ${k.parent.value}, and ${k.parent.parent.value} change colors`)
          k = k.parent.parent
        } else {
          if (k === k.parent.right) {
            // Case 2: Uncle is black and node is a right child
            k = k.parent
            this.logs.push(`Left rotation: Ant ${k.value} moves up`)
            this.leftRotate(k)
          }
          // Case 3: Uncle is black and node is a left child
          if (k.parent !== null && k.parent.parent !== null) {
            k.parent.color = "black"
            k.parent.parent.color = "red"
            this.logs.push(`Right rotation: Ant ${k.parent.parent.value} steps down`)
            this.rightRotate(k.parent.parent)
          }
        }
      }

      if (k === this.root) {
        break
      }
    }

    if (this.root) {
      this.root.color = "black"
      this.logs.push("Queen Ant ensures her royal black status")
    }
  }

  // Left rotation
  leftRotate(x) {
    const y = x.right
    if (!y) return

    x.right = y.left

    if (y.left !== this.NIL) {
      y.left.parent = x
    }

    y.parent = x.parent

    if (x.parent === null) {
      this.root = y
    } else if (x === x.parent.left) {
      x.parent.left = y
    } else {
      x.parent.right = y
    }

    y.left = x
    x.parent = y
  }

  // Right rotation
  rightRotate(y) {
    const x = y.left
    if (!x) return

    y.left = x.right

    if (x.right !== this.NIL) {
      x.right.parent = y
    }

    x.parent = y.parent

    if (y.parent === null) {
      this.root = x
    } else if (y === y.parent.left) {
      y.parent.left = x
    } else {
      y.parent.right = x
    }

    x.right = y
    y.parent = x
  }

  // Calculate positions for visualization
  calculatePositions() {
    const nodeWidth = 60
    const levelHeight = 100
    const nodes = []

    if (!this.root || this.root === this.NIL) return nodes

    // Calculate width of the tree
    const getWidth = (node, level) => {
      if (node === null || node === this.NIL) return 0
      return getWidth(node.left, level + 1) + 1 + getWidth(node.right, level + 1)
    }

    const treeWidth = getWidth(this.root, 0) * nodeWidth

    // Assign positions to nodes
    const assignPositions = (node, level, leftBound, rightBound) => {
      if (node === null || node === this.NIL) return

      const x = (leftBound + rightBound) / 2
      const y = level * levelHeight + 50

      node.x = x
      node.y = y

      nodes.push(node)

      assignPositions(node.left, level + 1, leftBound, x)
      assignPositions(node.right, level + 1, x, rightBound)
    }

    assignPositions(this.root, 0, 0, treeWidth)
    return nodes
  }

  // Get all edges for visualization
  getEdges() {
    const edges = []

    const traverse = (node) => {
      if (node === null || node === this.NIL) return

      if (node.left !== this.NIL) {
        edges.push({ from: node, to: node.left })
        traverse(node.left)
      }

      if (node.right !== this.NIL) {
        edges.push({ from: node, to: node.right })
        traverse(node.right)
      }
    }

    traverse(this.root)
    return edges
  }
}

// Node component
const Node = React.forwardRef(({ value, color, x, y, id }, ref) => {
  return (
    <div ref={ref} className={`node ${color === "black" ? "node-black" : "node-red"}`} style={{ left: x, top: y }}>
      <span className="node-emoji" role="img" aria-label="ant">
        {color === "black" ? "🐜" : "🐜"}
      </span>
      <span className={`node-value ${color === "black" ? "node-value-black" : "node-value-red"}`}>{value}</span>
    </div>
  )
})
Node.displayName = "Node"

// Edge component
const Edge = React.forwardRef(({ from, to }, ref) => {
  const fromX = from.x + 32
  const fromY = from.y + 32
  const toX = to.x + 32
  const toY = to.y + 32

  return <line ref={ref} x1={fromX} y1={fromY} x2={toX} y2={toY} stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
})
Edge.displayName = "Edge"

// Alert component for duplicate warnings
const Alert = ({ message, onClose }) => {
  const alertRef = useRef(null)

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(alertRef.current, { opacity: 0, y: -20 }, { opacity: 1, y: 0, duration: 0.3 })

      // Auto dismiss after 3 seconds
      const timer = setTimeout(() => {
        if (alertRef.current) {
          gsap.to(alertRef.current, {
            opacity: 0,
            y: -20,
            duration: 0.3,
            onComplete: onClose,
          })
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [onClose])

  return (
    <div ref={alertRef} className="alert">
      <div className="alert-content">
        <span role="img" aria-label="warning">
          ⚠️
        </span>{" "}
        {message}
      </div>
      <Button variant="" size="sm" className="alert-close" onClick={onClose}>
        ×
      </Button>
    </div>
  )
}

// Right Sidebar Content Component
const RightSidebarContent = () => {
  return (
    <>
      <h2 className="sidebar-title">How It Works</h2>

      <div className="info-card">
        <h3 className="info-title">Red-Black Ant Colony</h3>
        <p className="info-text">Our ant colony follows special rules to stay organized:</p>
        <ul className="info-list">
          <li>
            Each ant is either <span className="red-text">red</span> or <span className="bold-text">black</span>.
          </li>
          <li>
            The queen ant (root) is always <span className="bold-text">black</span>.
          </li>
          <li>
            <span className="red-text">Red</span> ants can't have <span className="red-text">red</span> children.
          </li>
          <li>
            Every path from an ant to a leaf has the same number of <span className="bold-text">black</span> ants.
          </li>
          <li>
            Each ant must have a <span className="bold-text">unique number</span> - no duplicates allowed!
          </li>
        </ul>
      </div>

      <div className="info-card">
        <h4 className="info-subtitle">Colony Organization:</h4>
        <ul className="info-list">
          <li>
            <span className="bold-text">Color Change:</span> Ants change color to maintain balance
          </li>
          <li>
            <span className="bold-text">Left Shuffle:</span> Ants move left to reorganize
          </li>
          <li>
            <span className="bold-text">Right Shuffle:</span> Ants move right to reorganize
          </li>
        </ul>
      </div>

      <div className="info-card">
        <h4 className="info-subtitle">Why This Matters:</h4>
        <p className="info-text small">
          This organization keeps our ant colony efficient. It ensures that no matter how many ants join or leave, we
          can always find any ant quickly!
        </p>
      </div>
    </>
  )
}

// Main component
const RedBlackTreeVisualizer = () => {
  const [tree] = useState(new RedBlackTree())
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const [logs, setLogs] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [currentStep, setCurrentStep] = useState(null)
  const [alert, setAlert] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const nodesRef = useRef({})
  const edgesRef = useRef({})
  const svgRef = useRef(null)
  const sidebarRef = useRef(null)
  const sidebarButtonRef = useRef(null)

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev)
  }

  // Animate sidebar
  useEffect(() => {
    if (sidebarRef.current) {
      if (sidebarOpen) {
        gsap.to(sidebarRef.current, {
          x: 0,
          duration: 0.3,
          ease: "power2.out",
        })
      } else {
        gsap.to(sidebarRef.current, {
          x: "100%",
          duration: 0.3,
          ease: "power2.in",
        })
      }
    }
  }, [sidebarOpen])

  // Update the visualization
  const updateVisualization = () => {
    const calculatedNodes = tree.calculatePositions()
    const calculatedEdges = tree.getEdges()
    setNodes([...calculatedNodes])
    setEdges([...calculatedEdges])
    setLogs([...tree.logs])
  }

  // Animate the tree formation
  useEffect(() => {
    if (nodes.length === 0) return

    // Animate nodes
    nodes.forEach((node) => {
      const nodeElement = nodesRef.current[node.id]
      if (nodeElement) {
        gsap.from(nodeElement, {
          opacity: 0,
          scale: 0,
          duration: 0.5,
          ease: "back.out(1.7)",
        })
      }
    })

    // Animate edges
    edges.forEach((edge, index) => {
      const edgeElement = edgesRef.current[`edge-${index}`]
      if (edgeElement) {
        gsap.from(edgeElement, {
          strokeDashoffset: edgeElement.getTotalLength(),
          duration: 0.5,
          ease: "power2.out",
        })
      }
    })
  }, [nodes, edges])

  // Insert a new value
  const handleInsert = (value) => {
    // Try to insert the value
    const success = tree.insert(value)

    if (success) {
      setCurrentStep(`Ant ${value} is joining the colony!`)
      updateVisualization()
      setTimeout(() => {
        setCurrentStep(null)
      }, 2000)
    } else {
      // Show duplicate warning
      setAlert(`Ant ${value} already exists in the colony!`)
      updateVisualization() // Update logs
    }
  }

  // Insert a random value
  const insertRandom = () => {
    const randomValue = Math.floor(Math.random() * 100)
    handleInsert(randomValue)
  }

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    const value = Number.parseInt(inputValue)
    if (!isNaN(value)) {
      handleInsert(value)
      setInputValue("")
    }
  }

  // Dismiss alert
  const dismissAlert = () => {
    setAlert(null)
  }

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        sidebarButtonRef.current &&
        !sidebarButtonRef.current.contains(event.target)
      ) {
        setSidebarOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [sidebarOpen])

  return (
    <div className="rbtcontainer">
      {/* Alert for duplicate warnings */}
      {alert && <Alert message={alert} onClose={dismissAlert} />}

      {/* Left Sidebar - Comparison Logs */}
      <div className="red-black-sidebar left-sidebar">
        <h2 className="sidebar-title">Ant Colony Activity</h2>

        <form onSubmit={handleSubmit} className="form">
          <div className="input-group">
            <input
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Enter ant number"
              className="input"
            />
            <Button type="submit" variant="" className="button add-button">
              Add Ant
            </Button>
          </div>
        </form>

        <Button variant="" className="button random-button" onClick={insertRandom}>
          Add Random Ant
        </Button>

        <h3 className="section-title">Colony Activity Log</h3>
        <div className="log-rbtcontainer">
          {logs.length === 0 ? (
            <p className="empty-log">No activity yet. Add some ants!</p>
          ) : (
            logs.map((log, index) => (
              <p key={index} className="log-entry">
                {log.includes("joins")
                  ? "🐜 "
                  : log.includes("rotation")
                    ? "🔄 "
                    : log.includes("already exists")
                      ? "⚠️ "
                      : "🎨 "}
                {log}
              </p>
            ))
          )}
        </div>
      </div>

      {/* Visualization */}
      <div className="visualization">
        {currentStep && <div className="step-indicator">{currentStep}</div>}

        <svg ref={svgRef} className="svg-rbtcontainer">
          {edges.map((edge, index) => (
            <Edge
              key={`edge-${index}`}
              ref={(el) => (edgesRef.current[`edge-${index}`] = el)}
              from={edge.from}
              to={edge.to}
            />
          ))}
        </svg>

        {nodes.map((node) => (
          <Node
            key={node.id}
            ref={(el) => (nodesRef.current[node.id] = el)}
            id={node.id}
            value={node.value}
            color={node.color}
            x={node.x}
            y={node.y}
          />
        ))}

        {/* Right Sidebar Toggle Button */}
        <Button
          ref={sidebarButtonRef}
          variant=""
          className={`sidebar-toggle-button ${sidebarOpen ? "active" : ""}`}
          onClick={toggleSidebar}
          aria-label="Toggle information sidebar"
        >
          <span className="sidebar-toggle-icon">{sidebarOpen ? "×" : "ℹ️"}</span>
          <span className="sidebar-toggle-text">{sidebarOpen ? "Close" : "How It Works"}</span>
        </Button>

        {/* Right Sidebar - How It Works (Popup) */}
        <div
          ref={sidebarRef}
          className="red-black-sidebar right-sidebar-popup"
          style={{ transform: "translateX(100%)" }}
        >
          <RightSidebarContent />
        </div>
      </div>
    </div>
  )
}

export default RedBlackTreeVisualizer
